import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ActivatedRoute } from "@angular/router";
import { first } from "rxjs/operators";
import { TranslocoService } from "@jsverse/transloco";

import { RegexPatterns } from "../../../../shared/constants/regex-patterns.constants";
import { LorenInputTypes } from "../../../../shared/modules/loren-entity-form/components/loren-entity-form-field/constants/loren-input-types.constants";
import { LorenFieldTypes } from "../../../../shared/modules/loren-entity-form-card/enums/loren-field-types.enum";
import { LorenEntityFormModel } from "../../../../shared/modules/loren-entity-form-card/interfaces/loren-entity-form-model.interface.ts";
import { LorenFormSubmission } from "../../../../shared/modules/loren-entity-form-card/interfaces/loren-form-submission.interface";
import { LorenPasswordResetToken } from "../../interfaces/loren-password-reset-token.interface";
import { LorenHttpResponseError } from "../../../../shared/interfaces/loren-http-response-error.interface";
import { LorenHttpResponse } from "../../../../shared/interfaces/loren-http-response.interface";
import { LorenBaseFeature } from "../../../../shared/base-components/loren-base-feature/loren-base-feature";
import { LorenHttpErrorTranslationService } from "../../../../shared/services/loren-http-error-translation-service/loren-http-error-translation.service";
import { LorenAuthenticationEndpointsService } from "../../../../shared/services/loren-authentication-service/loren-authentication-endpoints.service";

@Component({
    selector: "loren-password-reset",
    templateUrl: "./loren-password-reset.component.html",
    styleUrls: ["./loren-password-reset.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LorenPasswordResetComponent
  extends LorenBaseFeature
  implements OnInit, OnDestroy
{
  private activatedRoute = inject(ActivatedRoute);
  private lorenHttpErrorTranslationService = inject(LorenHttpErrorTranslationService);
  private lorenAuthenticationEndpointsService = inject(LorenAuthenticationEndpointsService);
  private translateService = inject(TranslocoService);
  private http = inject(HttpClient);

  constructor() {
    super();

    document.body.className = !this.isHandset
      ? "login-background"
      : "login-background-mobile";
  }

  private static readonly NEW_PASSWORD_FIELD_NAME: string = "new_password";
  private static readonly NEW_PASSWORD_CONFIRM_FIELD_NAME: string =
    "confirm_new_password";

  passwordChanged: boolean;
  userChangePasswordFormModel: LorenEntityFormModel;
  errors: string[] = [];
  disableSubmitBtn: boolean;
  validResetSession = false;
  resetToken: LorenPasswordResetToken;

  ngOnInit(): void {
    this.activatedRoute.data.subscribe((res) => {
      this.resetToken = res.token as LorenPasswordResetToken;
      if (this.resetToken.valid) {
        this.buildUserChangePasswordFormModel();
        this.validResetSession = true;
      }
    });
  }

  ngOnDestroy(): void {
    document.body.className = "";
  }

  submit(formSubmission: LorenFormSubmission) {
    if (formSubmission.valid) {
      const newPassword =
        formSubmission.value[
          LorenPasswordResetComponent.NEW_PASSWORD_FIELD_NAME
        ];
      const newPasswordConfirmed =
        formSubmission.value[
          LorenPasswordResetComponent.NEW_PASSWORD_CONFIRM_FIELD_NAME
        ];
      if (newPassword === newPasswordConfirmed) {
        this.http
          .post(
            this.lorenAuthenticationEndpointsService.forgottenPasswordReset(),
            {
              newPassword,
              token: this.resetToken.token,
            }
          )
          .subscribe(
            (res) => {
              const response = res as LorenHttpResponse;
              if (response.success) {
                this.passwordChanged = true;
              }
            },
            (error) => {
              if (error.error.errors) {
                const responseErrors = error.error
                  .errors as LorenHttpResponseError[];
                this.errors =
                  this.lorenHttpErrorTranslationService.getTranslatedErrorsFromResponse(
                    responseErrors
                  );
              } else {
                this.translateService
                  .selectTranslate("errors.internal_server_error")
                  .pipe(first())
                  .subscribe((translation) => {
                    this.errors = [translation];
                  });
              }
            }
          );
      } else {
        this.translateService
          .selectTranslate("errors.new_password_mismatch")
          .pipe(first())
          .subscribe((translation) => {
            this.errors = [translation];
          });
      }
    }
  }

  private buildUserChangePasswordFormModel() {
    this.userChangePasswordFormModel = {
      formName: "",
      formFields: [
        {
          name: LorenPasswordResetComponent.NEW_PASSWORD_FIELD_NAME,
          label: "user.new_password",
          formControlType: LorenFieldTypes.INPUT,
          validator: {
            required: true,
            minLength: 6,
            maxLength: 30,
            pattern: RegexPatterns.PASSWORD,
          },
          hint: "user.password_requirements",
          translateHint: true,
          inputType: LorenInputTypes.PASSWORD,
          options: [],
        },
        {
          name: LorenPasswordResetComponent.NEW_PASSWORD_CONFIRM_FIELD_NAME,
          label: "user.confirm_new_password",
          formControlType: LorenFieldTypes.INPUT,
          validator: {
            required: true,
          },
          inputType: LorenInputTypes.PASSWORD,
          options: [],
        },
      ],
    };
  }
}
